// Damage titles, plain-language descriptions and default costs, shared by the
// landlord's report and the tenant's shared view so both read the same.

export const DAMAGE_COPY = {
  drywall_puncture: {
    title: 'Drywall Puncture',
    description: 'Circular penetration in drywall, approx. 2-4 inches in diameter. Exceeds standard picture-hanging wear.',
    tag: 'Impact Damage',
    defaultCost: 75,
  },
  carpet_discoloration: {
    title: 'Carpet Discoloration',
    description: 'Irregular dark discoloration on flooring. Edge definition suggests liquid spill. High contrast to surrounding area.',
    tag: 'Stain / Spill',
    defaultCost: 120,
  },
  appliance_component_missing: {
    title: 'Appliance Component Missing',
    description: 'Standard component not detected in designated housing. Surrounding hardware appears intact.',
    tag: 'Missing Item',
    defaultCost: 45,
  },
  door_trim_scuffing: {
    title: 'Door/Trim Scuffing',
    description: 'Multiple parallel scratches on lower surface. Paint layer removed exposing base material.',
    tag: 'Pet Damage',
    defaultCost: 85,
  },
  blind_window_damage: {
    title: 'Blind/Window Treatment Damage',
    description: 'Structural bend or snapping detected in horizontal slats. Does not align with standard operational wear.',
    tag: 'Neglect',
    defaultCost: 35,
  },
  excessive_surface_debris: {
    title: 'Excessive Surface Debris',
    description: 'High accumulation of surface debris or residue. Area requires professional mitigation.',
    tag: 'Cleaning Required',
    defaultCost: 150,
  },
  burn_heat_damage: {
    title: 'Burn Mark / Heat Damage',
    description: 'Localized severe discoloration and material charring. Indicates exposure to elevated heat.',
    tag: 'Burn / Heat',
    defaultCost: 90,
  },
};

const TYPE_ALIASES = {
  hole: 'drywall_puncture',
  crack: 'drywall_puncture',
  stain: 'carpet_discoloration',
  water_damage: 'carpet_discoloration',
  missing_item: 'appliance_component_missing',
  missing_component: 'appliance_component_missing',
  scratch: 'door_trim_scuffing',
  scuff: 'door_trim_scuffing',
  blinds_damage: 'blind_window_damage',
  cleaning: 'excessive_surface_debris',
  debris: 'excessive_surface_debris',
  burn: 'burn_heat_damage',
  heat_damage: 'burn_heat_damage',
};

// Coverage views for a room, flattened to 2D (no 3D viewer needed).
export const VIEW_OPTIONS = [
  { value: 'overview', label: 'Overview' },
  { value: 'left_wall', label: 'Left wall' },
  { value: 'right_wall', label: 'Right wall' },
  { value: 'back_wall', label: 'Back wall' },
  { value: 'front_wall', label: 'Front wall' },
  { value: 'floor', label: 'Floor' },
  { value: 'ceiling', label: 'Ceiling' },
  { value: 'detail', label: 'Detail / close-up' },
];

export const VIEW_LABELS_MAP = Object.fromEntries(VIEW_OPTIONS.map((v) => [v.value, v.label]));

export function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function money(v) {
  return `$${toNumber(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

export function formatType(type) {
  return String(type || 'Unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function canonicalType(type) {
  const raw = String(type || '').toLowerCase().trim();
  if (!raw) return '';
  return TYPE_ALIASES[raw] || raw;
}

export function damageProfile(damage) {
  const key = canonicalType(damage?.type);
  return DAMAGE_COPY[key] || {
    title: formatType(damage?.type),
    description: 'High-contrast irregular material variance detected. Manual validation recommended for chargeability classification.',
    tag: 'Neglect',
    defaultCost: 80,
  };
}
